import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, RotateCcw, Search, Trash2 } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { formatMoney } from "../../lib/money";
import { EmptyState } from "../../components/EmptyState";
import { Pagination, pageCount, usePageSize } from "../../components/Pagination";
import { StatusBadge } from "../../components/Badge";
import { RowMenu } from "../../components/RowMenu";
import type { RowMenuItem } from "../../components/RowMenu";
import { useAuth } from "../auth/useAuth";
import { DeleteQuoteDialog } from "./DeleteQuoteDialog";
import { QUOTE_IS_READ_ONLY_FOR_ROLE, canDeleteQuotes, canWriteQuotes } from "../../api/types";
import type { IsoDateTime, Paginated, QuoteSummary } from "../../api/types";
import { ARCHIVE_TABS } from "./archiveRules";
import type { ArchiveTab } from "./archiveRules";

// 封存區(設計規格 §9)。業主原話:「報價單>封存>封存內才可以刪除」。
//
// ── 這一頁是「刪得掉」的唯一入口 ──────────────────────────────────
//
// 封存與復原是**業務動作**(老闆與同仁,財務不行 —— 後端 `require_business`);
// **刪除只有老闆**,而且必須先封存、單號還要打對。
// 按不動的那幾顆一律是 disabled 並用 title 說明原因,**不藏起來** ——
// 藏起來只會讓人一直來問為什麼別人做得到自己做不到(與「顯示欄位」那顆鈕同一個
// 處理)。封存區本身則是全公司都看得到,財務也進得來:看得到不等於動得了
//(規格 §4.1;設計規格 §2.1 的權限矩陣)。
//
// ── 欄位是固定的,不讀「顯示欄位」設定 ──────────────────────────
//
// 日常列表要看哪幾欄由老闆在設定頁決定,封存區不跟著那一份設定走(規格 §9):
// 在這裡要看的是「什麼時候封的、誰封的」,與日常列表要看的不是同一組;而且那兩欄
// 在日常列表上永遠是空的,本來就不在可選清單裡。
//
// ── 搜尋送去後端,不在這一頁上篩 ────────────────────────────────
//
// 與報價單列表同一個理由:一頁只有 20 筆,在那 20 筆上面篩,打了關鍵字卻一筆
// 都沒中,只因為要找的那一張在第二頁 —— 畫面上看起來就像那張單已經被刪了。
//
// ── 關鍵字放在網址上 ────────────────────────────────────────────
//
// ⌘K 搜不到時那一行「封存區裡有 N 張符合的單」導到 `/quotes/archive?q=關鍵字`
// (CommandPalette 的 `goToArchive`)。那一行是規格 §6.1 留給使用者的唯一一條線:
// 封存的單已經從列表、日曆、搜尋、漏斗四處消失,線接不上,他看到的就是
// 「查無此單 → 點提示 → 還是找不到」,而那正是 §6.1 說要避免的
// 「以為系統把資料弄丟了」。
//
// 打字也同步回網址:重新整理、上一頁、把連結貼給同事,找的都還是同一疊單。

const SEARCH_DEBOUNCE_MS = 300;

/** 員工把滑鼠移到刪除上時看到的那一句。後端的 403 也是同一個規矩。 */
const DELETE_IS_OWNER_ONLY = "刪除只有老闆能做";

/**
 * 封存區的一列:日常列表那一組欄位,外加封存時間與封存者。
 *
 * 與後端的 `ArchivedQuoteSummary` 對得起來。封存者在後端就攤平成名字,
 * 這裡不拿 `archived_by_user_id` 再去查一次 —— 與 `owner_name` 同一條規矩。
 */
export interface ArchivedQuote extends QuoteSummary {
  archived_at: IsoDateTime | null;
  archived_by_name: string;
}

/** 表格固定這幾欄。順序就是畫出來的順序。 */
const COLUMNS: Array<{ key: string; label: string; align?: "right" }> = [
  { key: "quote_no", label: "單號" },
  { key: "title", label: "專案名稱" },
  { key: "client", label: "客戶" },
  { key: "status", label: "狀態" },
  { key: "total", label: "總計", align: "right" },
  { key: "archived_at", label: "封存時間" },
  { key: "archived_by", label: "封存者" },
];

/** 這幾欄是號碼、金額或時間,用等寬數字排版才不會一列一個寬度。 */
const TABULAR_COLUMNS = new Set(["quote_no", "total", "archived_at"]);

export function ArchivePage() {
  const { user } = useAuth();
  /**
   * 刪除只有老闆(業主原話,第 11 輪的規矩沒有因為多一個角色而鬆動)。
   *
   * 判斷走 `canDeleteQuotes`,不在這裡寫 `role === "OWNER"` —— 散出去的字串比對
   * 就是這一頁漏掉「復原」的原因:加角色的那一輪沒有一個地方會提醒你這裡也要改。
   */
  const canDelete = canDeleteQuotes(user?.role);
  /**
   * 復原是**業務動作**(後端 `restore-from-archive` 掛 `require_business`),
   * 財務按不動。
   *
   * **只有在知道你是誰、而且你動不了的時候才擋。** `user` 是 null 只發生在單獨
   * render 這一頁的測試裡(真實動線在 `RequireAuth` 之後),與 `QuoteList` 同一條。
   */
  const readOnly = user != null && !canWriteQuotes(user.role);

  const [params, setParams] = useSearchParams();

  // keyword 是輸入框的即時值,query 是真的送出去的值。
  //
  // **兩個的初值都取自網址**:只給 keyword 的話,第一次 effect 會拿空字串打一趟
  // 沒有篩選的 API,畫面先閃出整份封存清單,等 debounce 到了才換成真正的結果。
  //
  // 只在掛載時讀一次(useState 的初值),之後由這一頁自己管 —— 每次 render 都
  // 回頭讀網址的話,底下那個「打字同步回網址」的 effect 會跟它打架。
  // 三個分頁:已結案 / 已取消 / 草稿(業主 2026-10-16)。網址的 ?status= 決定開哪一頁,預設已結案
  const tabParam = params.get("status");
  const tab: ArchiveTab = ARCHIVE_TABS.some((t) => t.key === tabParam) ? (tabParam as ArchiveTab) : "CLOSED";
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [keyword, setKeyword] = useState(() => params.get("q") ?? "");
  const [query, setQuery] = useState(() => params.get("q") ?? "");
  const [data, setData] = useState<Paginated<ArchivedQuote> | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // 撈第幾次。復原或刪除之後 +1,用來把清單重撈一遍。
  const [reloads, setReloads] = useState(0);
  // 動作本身的錯誤(復原失敗)。與「清單載入失敗」分開講,那是兩件事。
  const [actionError, setActionError] = useState<string | null>(null);
  // 正在確認刪除的那一張。null 就是沒有視窗開著。
  const [deleting, setDeleting] = useState<ArchivedQuote | null>(null);

  // 復原或刪除之後,那一列連同它的 ⋮ 一起消失,焦點的宿主被移除 ——
  // 不接手的話焦點掉回 <body>,鍵盤使用者下一次按 Tab 會從整份文件最上面重來
  // (RowMenu 檔頭規矩 5)。交給搜尋框:它一定還在,而且剛做完一個動作,
  // 下一步多半就是再找一張。
  const searchRef = useRef<HTMLInputElement | null>(null);
  // 每接手一次 +1。**用 effect 而不是當場 focus()**:刪除視窗卸載時
  // `useRestoreFocus` 會把焦點還給那顆馬上要消失的 ⋮,而卸載的清理跑在這個
  // effect 之前 —— 當場 focus() 會被它蓋回去。
  const [focusHandoffs, setFocusHandoffs] = useState(0);

  // 分頁。換關鍵字或每頁筆數就回第 1 頁,而且在 render 當下換(理由見 QuoteList)。
  const [pageSize, savePageSize] = usePageSize("nebula.pageSize.archive");
  const [paging, setPaging] = useState({ key: "", page: 1 });
  const pagingKey = JSON.stringify([query, pageSize, tab]);
  let page = paging.page;
  if (paging.key !== pagingKey) {
    page = 1;
    setPaging({ key: pagingKey, page });
  }
  const setPage = (next: number) => setPaging((prev) => ({ ...prev, page: next }));

  const reload = useCallback(() => setReloads((n) => n + 1), []);
  const handOffFocus = useCallback(() => setFocusHandoffs((n) => n + 1), []);

  useEffect(() => {
    if (focusHandoffs === 0) return;
    searchRef.current?.focus();
  }, [focusHandoffs]);

  useEffect(() => {
    if (keyword === query) return;
    const timer = window.setTimeout(() => setQuery(keyword), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [keyword, query]);

  // 真的送出去的那個關鍵字寫回網址。跟著 query 而不是 keyword —— 每打一個字就
  // 改一次網址,上一頁要按二十次才回得去。
  //
  // `replace: true` 同一個理由:搜尋的每一次改字不是一段值得回頭的瀏覽歷程。
  // 相同就不寫,否則這個 effect 會自己觸發自己。
  useEffect(() => {
    if ((params.get("q") ?? "") === query) return;
    const next = new URLSearchParams(params);
    // 清空就把參數整個拿掉,不留一個空的 `?q=` 掛在網址上。
    if (query === "") next.delete("q");
    else next.set("q", query);
    setParams(next, { replace: true });
  }, [query, params, setParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<Paginated<ArchivedQuote> & { counts?: Record<string, number> }>("/quotes/archived", { q: query, status: tab, page, size: pageSize })
      .then((result) => {
        if (cancelled) return;
        // 復原或刪掉最後一頁唯一那張之後,退回最後一頁,不秀一張空表。
        const lastPage = pageCount(result?.total ?? 0, pageSize);
        if (page > lastPage && (result?.total ?? 0) > 0) {
          setPaging((prev) => ({ ...prev, page: lastPage }));
          return;
        }
        setData(result);
        setCounts(result?.counts ?? {});
        setFailed(false);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, page, pageSize, reloads, tab]);

  const pickTab = (next: ArchiveTab) => {
    const p = new URLSearchParams(params);
    p.set("status", next);
    setParams(p, { replace: true });
  };

  /**
   * 從封存區撈回來。走的是 `restore-from-archive`,**不是** `/restore` ——
   * 後者是「把內容退回三版之前」,兩個都叫復原,叫錯那一支會把客戶談好的內容蓋掉。
   */
  const restore = async (quote: ArchivedQuote) => {
    setActionError(null);
    try {
      await api.post(`/quotes/${quote.id}/restore-from-archive`);
      reload();
      handOffFocus();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "復原失敗,請稍後再試");
    }
  };

  // 後端萬一回了非預期的形狀,也不能讓清單整個炸掉。
  const items: ArchivedQuote[] = data && Array.isArray(data.items) ? data.items : [];
  const searching = query.trim() !== "";

  /** 一格的內容。 */
  const cellOf = (key: string, quote: ArchivedQuote): ReactNode => {
    switch (key) {
      case "quote_no":
        // 封存的單用網址指名照樣打得開(規格 §6.1),進去頂上會有封存橫幅。
        return (
          <Link
            to={`/quotes/${quote.id}`}
            className="rounded-[var(--radius)] font-medium text-[var(--accent)] hover:text-[var(--accent-hover)] hover:underline"
          >
            {quote.quote_no}
          </Link>
        );
      case "title":
        return quote.title;
      case "client":
        return quote.client_name;
      case "status":
        return <StatusBadge status={quote.status} />;
      case "total":
        return formatMoney(quote.total);
      case "archived_at":
        return formatTaipeiDateTime(quote.archived_at);
      case "archived_by":
        // 封存的人已經離職時後端給空字串,補一個破折號,免得那一格看起來像沒載入完。
        return quote.archived_by_name || "—";
      default:
        return "";
    }
  };

  /**
   * 一列的兩個動作。**兩顆都可能按不動,而且兩顆都畫得出來、說得出原因。**
   *
   * 復原對財務、刪除對非老闆 —— 理由不同,處置一樣:藏起來只會讓人一直來問
   * 為什麼別人做得到自己做不到(與封存區這一頁的檔頭同一條規矩)。
   */
  const menuItemsFor = (quote: ArchivedQuote): RowMenuItem[] => [
    {
      key: "restore",
      label: "復原",
      icon: <RotateCcw aria-hidden="true" size={16} />,
      disabled: readOnly,
      disabledReason: readOnly ? QUOTE_IS_READ_ONLY_FOR_ROLE : undefined,
      onSelect: () => void restore(quote),
    },
    {
      key: "delete",
      label: "刪除",
      icon: <Trash2 aria-hidden="true" size={16} />,
      danger: true,
      disabled: !canDelete,
      disabledReason: canDelete ? undefined : DELETE_IS_OWNER_ONLY,
      // 刪完那一列連同 ⋮ 一起不見,RowMenu 不要把焦點還給它 ——
      // 焦點由這一頁接手(見 handOffFocus)。
      destroysTrigger: true,
      onSelect: () => setDeleting(quote),
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div role="tablist" aria-label="封存的類別" className="flex gap-1 border-b border-[var(--border)]">
        {ARCHIVE_TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            onClick={() => pickTab(item.key)}
            className={
              "-mb-px border-b-2 px-3 py-1.5 text-sm " +
              (tab === item.key
                ? "border-[var(--accent)] font-medium text-[var(--text)]"
                : "border-transparent text-[var(--text-2)] hover:text-[var(--text)]")
            }
          >
            {item.label}
            <span className="ml-1 tabular-nums text-xs text-[var(--text-3)]">{counts[item.key] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[12rem] flex-1 items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 focus-within:border-[var(--accent)]">
          <Search aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
          <input
            type="search"
            ref={searchRef}
            value={keyword}
            aria-label="搜尋封存的報價單"
            placeholder="搜尋單號、專案名稱、客戶,或單子裡的明細"
            onChange={(event) => setKeyword(event.target.value)}
            // 不要加 outline-none:焦點環是鍵盤使用者唯一看得出「我在哪」的線索。
            className="h-9 w-full min-w-0 border-0 bg-transparent text-sm text-[var(--text)] placeholder:text-[var(--text-3)]"
          />
        </div>
        {/* 走進封存區之後要走得回去。封存區是報價單底下的一層,不是側邊欄的另一項,
            所以回列表的路只有這一條。 */}
        <Link
          to="/quotes"
          className={
            "inline-flex h-9 items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] " +
            "bg-[var(--surface)] px-3.5 text-sm font-medium text-[var(--text)] hover:bg-[var(--surface-2)]"
          }
        >
          <ArrowLeft aria-hidden="true" size={16} />
          回報價單列表
        </Link>
      </div>

      {/* 封存區是「不要再出現在我眼前」的那一疊,不是垃圾桶:說明白它還在,
          而且撈得回來,否則沒有人敢按封存。 */}
      <p className="m-0 text-sm text-[var(--text-3)]">
        封存的報價單不會出現在列表、日曆、搜尋與漏斗選項裡,但內容都還在,隨時可以復原。
      </p>

      {/* 財務進得來這一頁(封存清單只要登入就讀得到,他查舊單本來就要走這裡),
          但兩顆動作都按不動 —— 上面那一句「隨時可以復原」對他不成立,
          不說清楚的話他只會以為這一頁壞了。權限一律帶文字,不可以只靠反灰。 */}
      {readOnly ? (
        <div
          role="status"
          aria-label="權限狀態"
          className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 py-2"
        >
          <Lock aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
          <span className="min-w-0 flex-1 text-sm text-[var(--text)]">
            {`${QUOTE_IS_READ_ONLY_FOR_ROLE}:封存的單看得到內容與金額,但不能復原,也不能刪除。`}
          </span>
        </div>
      ) : null}

      {loading && data === null ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}

      {failed ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          封存區載入失敗,請稍後再試。
        </p>
      ) : null}

      {actionError ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          {actionError}
        </p>
      ) : null}

      {/* 搜不到與「一張都沒封過」是兩件事。混成同一句,使用者會以為封存的單被弄丟了。 */}
      {!loading && !failed && items.length === 0 && searching ? (
        <p className="m-0 text-sm text-[var(--text-2)]">沒有符合「{query}」的封存報價單。</p>
      ) : null}

      {!loading && !failed && items.length === 0 && !searching ? (
        <EmptyState
          title="目前沒有封存的報價單"
          description="在報價單列表的列選單裡按「封存」,那張單就會收到這裡來。"
        />
      ) : null}

      {!failed && items.length > 0 ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="封存的報價單" className="w-full min-w-[56rem] border-collapse text-left">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={`px-3 py-2 ${column.align === "right" ? "text-right" : ""}`}
                  >
                    {column.label}
                  </th>
                ))}
                {/* ⋮ 那一欄沒有標題可寫,但表頭不能空著:報讀器逐欄唸過來時,
                    一個沒有名字的欄位只會唸出「欄」。 */}
                <th scope="col" className="px-3 py-2 text-right">
                  <span className="sr-only">動作</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((quote) => (
                <tr key={quote.id} className="border-t border-[var(--border)]">
                  {COLUMNS.map((column) => (
                    <td
                      key={column.key}
                      className={[
                        "px-3 py-2 text-sm",
                        TABULAR_COLUMNS.has(column.key) ? "tabular" : "",
                        column.align === "right" ? "text-right" : "",
                        column.key === "title" ? "text-[var(--text)]" : "text-[var(--text-2)]",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {cellOf(column.key, quote)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <RowMenu items={menuItemsFor(quote)} label={`${quote.quote_no} 更多動作`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {data && items.length > 0 ? (
        <Pagination total={data.total} page={page} size={pageSize} onPageChange={setPage} onSizeChange={savePageSize} />
      ) : null}

      {/* 沒有目標就整個不渲染,而且 key 用 quote.id ——
          關掉就卸載、換一張單就換一個元件實例,上一次打過的單號留不住。
          這一條是整個防呆機制的地基,詳見 DeleteQuoteDialog 的檔頭。 */}
      {deleting !== null ? (
        <DeleteQuoteDialog
          key={deleting.id}
          quote={deleting}
          onCancel={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            reload();
            handOffFocus();
          }}
        />
      ) : null}
    </div>
  );
}
